import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { streams } from '../../lib/api';
import signalRService from '../../lib/signalr';

function PerformerDashboard() {
  const { id } = useParams();
  const streamId = parseInt(id);
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [reactions, setReactions] = useState({ like: 0, clap: 0, heart: 0, fire: 0 });
  const [floatingReactions, setFloatingReactions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => { loadStream(); }, [id]);

  useEffect(() => {
    if (!streamId) return;
    const cleanups = [];
    const setup = async () => {
      cleanups.push(await signalRService.subscribeToStream(streamId, (data) => {
        setStream(prev => prev ? { ...prev, status: data.status } : null);
      }));
      cleanups.push(await signalRService.onViewerCount(streamId, (data) => {
        setViewerCount(data.count);
      }));
      cleanups.push(await signalRService.onReaction(streamId, (data) => {
        setReactions(prev => ({ ...prev, [data.reactionType]: (prev[data.reactionType] || 0) + 1 }));
        // Add floating reaction
        const floatId = Date.now() + Math.random();
        setFloatingReactions(prev => [...prev, { id: floatId, type: data.reactionType, x: 20 + Math.random() * 60 }]);
        setTimeout(() => setFloatingReactions(prev => prev.filter(r => r.id !== floatId)), 3000);
      }));
      cleanups.push(await signalRService.onMessage(streamId, (data) => {
        setMessages(prev => [...prev.slice(-49), data]);
      }));
    };
    setup();
    return () => cleanups.forEach(fn => fn?.());
  }, [streamId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fullscreen handling
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const loadStream = async () => {
    try {
      const res = await streams.getById(streamId);
      setStream(res.data.stream);
    } catch (err) {
      console.error('Failed to load stream:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;
    await signalRService.sendPerformerMessage(streamId, messageText.trim());
    setMessageText('');
  };

  const EMOJI = { like: '👍', clap: '👏', heart: '❤️', fire: '🔥' };

  if (loading) return <p className="text-neutral-500">Loading...</p>;
  if (!stream) return <p className="text-neutral-500">Stream not found.</p>;

  const isLive = stream.status === 'running';

  return (
    <div
      ref={containerRef}
      className={`${isFullscreen ? 'fixed inset-0 z-[9999]' : 'max-w-5xl'} bg-neutral-950 text-white flex flex-col min-h-[80vh] rounded-xl overflow-hidden`}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 bg-neutral-900/80">
        {!isFullscreen && (
          <Link to={`/streams/${streamId}`} className="text-neutral-500 hover:text-white text-sm">&larr;</Link>
        )}
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${isLive ? 'bg-red-500 animate-pulse' : 'bg-neutral-600'}`} />
          <span className="font-semibold text-sm">{stream.title || stream.name}</span>
        </div>
        <button
          onClick={toggleFullscreen}
          className="px-3 py-1 text-xs rounded-lg bg-neutral-800 hover:bg-neutral-700 transition-colors"
        >
          {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center relative px-6 py-4">
        {/* Floating reactions */}
        {floatingReactions.map(r => (
          <span
            key={r.id}
            className="absolute text-5xl pointer-events-none animate-[floatUp_3s_ease-out_forwards]"
            style={{ left: `${r.x}%`, bottom: '30%' }}
          >
            {EMOJI[r.type]}
          </span>
        ))}

        {/* Massive viewer count */}
        <div className="text-center mb-8">
          <span className="text-neutral-500 text-lg">👁</span>
          <p className={`font-bold leading-none ${isFullscreen ? 'text-[10rem]' : 'text-[6rem]'}`}>
            {viewerCount}
          </p>
          <p className="text-neutral-500 text-sm uppercase tracking-widest mt-2">viewers</p>
        </div>

        {/* Reaction counters */}
        <div className={`flex gap-6 ${isFullscreen ? 'gap-12' : ''}`}>
          {Object.entries(EMOJI).map(([type, emoji]) => (
            <div key={type} className="text-center">
              <span className={isFullscreen ? 'text-6xl' : 'text-4xl'}>{emoji}</span>
              <p className={`font-bold mt-1 ${isFullscreen ? 'text-4xl' : 'text-2xl'}`}>{reactions[type]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Messages feed */}
      <div className="px-6 max-h-48 overflow-y-auto space-y-1">
        {messages.map((msg, i) => (
          <div key={i} className={`text-sm ${isFullscreen ? 'text-lg' : ''} ${msg.isFromPerformer ? 'text-blue-400' : 'text-neutral-300'}`}>
            <span className="font-semibold text-neutral-500 mr-2">
              {msg.isFromPerformer ? '📢' : msg.displayName}
            </span>
            {msg.text}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Performer message input */}
      <form onSubmit={handleSendMessage} className="flex gap-2 px-6 py-4 bg-neutral-900/80">
        <input
          type="text"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder="Send message to audience..."
          maxLength={500}
          className={`flex-1 px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${isFullscreen ? 'text-xl' : 'text-sm'}`}
        />
        <button
          type="submit"
          disabled={!messageText.trim()}
          className={`px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors ${isFullscreen ? 'text-xl' : 'text-sm'}`}
        >
          Send
        </button>
      </form>

      {/* CSS animation for floating reactions */}
      <style>{`
        @keyframes floatUp {
          0% { opacity: 1; transform: translateY(0) scale(1); }
          50% { opacity: 0.8; transform: translateY(-100px) scale(1.2); }
          100% { opacity: 0; transform: translateY(-250px) scale(0.8); }
        }
      `}</style>
    </div>
  );
}

export default PerformerDashboard;
